import clsx from 'clsx';
import React, { useCallback, useEffect } from 'react'
import { Offcanvas } from 'react-bootstrap'
import { useForm } from 'react-hook-form';
import { CreateStockItemFormData, CreateStockItemSchema } from '../schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { StockItem } from '../types';
import { SAMPLE_CATEGORIES } from '../constants/sampleData';

interface EditStockItemDialogProps {
    show: boolean;
    onClose: () => void;
    item: StockItem | null;
    onSuccess?: () => void;
}

const EditStockItemDialog: React.FC<EditStockItemDialogProps> = ({ show, onClose, item, onSuccess }) => {
    const { register, handleSubmit, formState: { errors }, reset } = useForm<CreateStockItemFormData>({
        resolver: zodResolver(CreateStockItemSchema),
    });

    useEffect(() => {
        if (item) {
            reset({
                name: item.name,
                category: item.category,
                quantity: item.quantity,
                unit: item.unit,
                itemCode: item.itemCode,
                purchasingPrice: item.purchasingPrice,
                sellingPrice: item.sellingPrice,
            });
        }
    }, [item, reset]);

    const handleClose = () => {
        reset();
        onClose();
    }

    const onSubmit = useCallback((data: CreateStockItemFormData) => {
        toast.loading('Updating stock item...', { id: 'update-stock-item' });

        // TODO: Implement API call
        console.log('Updating stock item:', item?.id, data);

        // Simulate API call
        setTimeout(() => {
            handleClose();
            toast.success('Stock item updated successfully', { id: 'update-stock-item' });
            onSuccess?.();
        }, 1000);
    }, [item, onSuccess]);

    return (
        <Offcanvas show={show} onHide={handleClose} placement="end" className="offcanvas-responsive">
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>Edit Stock Item</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                <form onSubmit={handleSubmit(onSubmit)} className="d-flex flex-column" style={{ height: '100%' }}>
                    <div className="action-sheet-content flex-grow-1">
                        {/* Item Name */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="name" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Item Name: </label>
                            <div className="col-sm-8">
                                <input
                                    type="text"
                                    className={clsx("form-control app-form-control rounded-1", errors?.name && 'is-invalid')}
                                    id="name"
                                    placeholder="Item name"
                                    {...register('name')}
                                    style={{ fontSize: '14px', height: '35px' }}
                                />
                                {errors?.name && <div className="invalid-feedback">{errors.name.message}</div>}
                            </div>
                        </div>

                        {/* Category */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="category" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Category: </label>
                            <div className="col-sm-8">
                                <select
                                    className={clsx("form-control app-form-control rounded-1", errors?.category && 'is-invalid')}
                                    id="category"
                                    {...register('category')}
                                    style={{ fontSize: '14px', height: '35px' }}
                                >
                                    <option value="">Select category</option>
                                    {SAMPLE_CATEGORIES.map((category) => (
                                        <option key={category.id} value={category.name}>
                                            {category.name}
                                        </option>
                                    ))}
                                </select>
                                {errors?.category && <div className="invalid-feedback">{errors.category.message}</div>}
                            </div>
                        </div>

                        {/* Quantity & Unit */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="quantity" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Quantity: </label>
                            <div className="col-sm-8 d-flex">
                                <input
                                    type="number"
                                    className={clsx("form-control app-form-control rounded-1", errors?.quantity && 'is-invalid')}
                                    id="quantity"
                                    placeholder="0"
                                    {...register('quantity', { valueAsNumber: true })}
                                    style={{ fontSize: '14px', height: '35px', width: '80px' }}
                                />
                                <input
                                    type="text"
                                    className={clsx("form-control app-form-control rounded-1 ms-2", errors?.unit && 'is-invalid')}
                                    id="unit"
                                    placeholder="Unit (e.g., pcs, kg)"
                                    {...register('unit')}
                                    style={{ fontSize: '14px', height: '35px' }}
                                />
                            </div>
                        </div>
                        {(errors?.quantity || errors?.unit) && (
                            <div className="row mb-3">
                                <div className="col-sm-4"></div>
                                <div className="col-sm-8">
                                    {errors?.quantity && <div className="invalid-feedback d-block">{errors.quantity.message}</div>}
                                    {errors?.unit && <div className="invalid-feedback d-block">{errors.unit.message}</div>}
                                </div>
                            </div>
                        )}

                        {/* Item Code */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="itemCode" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Item Code: </label>
                            <div className="col-sm-8">
                                <input
                                    type="text"
                                    className={clsx("form-control app-form-control rounded-1", errors?.itemCode && 'is-invalid')}
                                    id="itemCode"
                                    placeholder="Optional item code"
                                    {...register('itemCode')}
                                    style={{ fontSize: '14px', height: '35px' }}
                                />
                                {errors?.itemCode && <div className="invalid-feedback">{errors.itemCode.message}</div>}
                            </div>
                        </div>

                        {/* Purchasing Price */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="purchasingPrice" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Purchase Price: </label>
                            <div className="col-sm-8">
                                <input
                                    type="number"
                                    step="0.01"
                                    className={clsx("form-control app-form-control rounded-1", errors?.purchasingPrice && 'is-invalid')}
                                    id="purchasingPrice"
                                    placeholder="0.00"
                                    {...register('purchasingPrice', { valueAsNumber: true })}
                                    style={{ fontSize: '14px', height: '35px' }}
                                />
                                {errors?.purchasingPrice && <div className="invalid-feedback">{errors.purchasingPrice.message}</div>}
                            </div>
                        </div>

                        {/* Selling Price */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="sellingPrice" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Selling Price: </label>
                            <div className="col-sm-8">
                                <input
                                    type="number"
                                    step="0.01"
                                    className={clsx("form-control app-form-control rounded-1", errors?.sellingPrice && 'is-invalid')}
                                    id="sellingPrice"
                                    placeholder="0.00"
                                    {...register('sellingPrice', { valueAsNumber: true })}
                                    style={{ fontSize: '14px', height: '35px' }}
                                />
                                {errors?.sellingPrice && <div className="invalid-feedback">{errors.sellingPrice.message}</div>}
                            </div>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="form-group mt-auto d-grid">
                        <button type="submit" className="btn btn-success btn-block">
                            Update Stock Item
                        </button>
                    </div>
                </form>
            </Offcanvas.Body>
        </Offcanvas>
    )
}

export default EditStockItemDialog